package com.example.mystybag.core.ui.theme

import androidx.compose.material3.ColorScheme
import androidx.compose.material3.Typography
import androidx.compose.material3.darkColorScheme
import androidx.compose.material3.lightColorScheme
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.example.mystybag.core.resources.SRC
import com.example.mystybag.common.resources.SR
import com.example.mystybag.core.expected.composeColor
import com.example.mystybag.core.expected.createFont

import com.example.mystybag.core.views.currentTheme

@Suppress("VARIABLE_IN_SINGLETON_WITHOUT_THREAD_LOCAL")
object LocalTheme {

    val carosoft = FontFamily(
        createFont(SRC.fonts.carossoft_bold,       weight = FontWeight.Bold),
        createFont(SRC.fonts.carossoft_medium,     weight = FontWeight.Medium),
        createFont(SRC.fonts.carossoft,            weight = FontWeight.Normal),
        createFont(SRC.fonts.carossoft_light,      weight = FontWeight.Light),
        createFont(SRC.fonts.carossoft_extralight, weight = FontWeight.ExtraLight)
    )

    /** Google Sans – used as default app typography (Display/Headline/Title/Body/Label scale). */
    val googleSans = FontFamily(
        createFont(SRC.fonts.google_sans_bold,      weight = FontWeight.Bold),
        createFont(SRC.fonts.google_sans_semibold,  weight = FontWeight.SemiBold),
        createFont(SRC.fonts.google_sans_medium,    weight = FontWeight.Medium),
        createFont(SRC.fonts.google_sans_regular,   weight = FontWeight.Normal)
    )

    val p0      = 0.dp
    val p2      = 2.dp
    val p4      = 4.dp
    val p6      = 6.dp
    val p8      = 8.dp
    val p12     = 12.dp
    val p16     = 16.dp
    val p20     = 20.dp
    val p24     = 24.dp
    val p28     = 28.dp
    val p32     = 32.dp
    val p40     = 40.dp
    val p44     = 44.dp
    val p48     = 48.dp
    val p52     = 52.dp

    val p56     = 56.dp
    val p64     = 64.dp
    val p72     = 72.dp
    val p76     = 76.dp

    val p05     = 0.5.dp
    val p1      = 1.dp
    val p3      = 3.dp
    val p5      = 5.dp
    val p7      = 7.dp
    val p9      = 9.dp
    val p10     = 10.dp
    val p13     = 13.dp
    val p14     = 14.dp
    val p15     = 15.dp
    val p17     = 17.dp
    val p18     = 18.dp
    val p19     = 19.dp
    val p21     = 21.dp
    val p22     = 22.dp
    val p25     = 25.dp
    val p26     = 26.dp
    val p29     = 29.dp
    val p30     = 30.dp
    val p33     = 33.dp
    val p34     = 34.dp
    val p36     = 36.dp
    val p38     = 38.dp
    val p42     = 42.dp
    val p45     = 45.dp
    val p46     = 46.dp
    val p50     = 50.dp
    val p54     = 54.dp
    val p55     = 55.dp
    val p60     = 60.dp
    val p65     = 65.dp
    val p70     = 70.dp
    val p77     = 77.dp
    val p80     = 80.dp
    val p84     = 84.dp
    val p89     = 89.dp
    val p92     = 92.dp
    val p96     = 96.dp
    val p100    = 100.dp
    val p114    = 114.dp
    val p124    = 124.dp
    val p150    = 150.dp
    val p152    = 152.dp
    val p184    = 184.dp
    val p200    = 200.dp
    val p244    = 244.dp
    val p230    = 230.dp
    val p300    = 300.dp
    val p400    = 400.dp
    val p500    = 500.dp

    /** Размер и отстъп на бутона X в диалоги – голям бутон, ясен отстъп от ръбовете (като на диалога за локация). */
    val dialogCloseButtonSizeDp = p36
    val dialogCloseButtonIconSizeDp = p24
    val dialogCloseButtonPaddingDp = p16
}

val defaultLightColorScheme = lightColorScheme(
    background              = SR.colors.sr_background.composeColor,
    onBackground            = SR.colors.sr_on_background.composeColor,
    primary                 = SR.colors.sr_primary.composeColor,
    onPrimary               = SR.colors.sr_on_primary.composeColor,
    primaryContainer        = SR.colors.sr_primary_container.composeColor,
    onPrimaryContainer      = SR.colors.sr_on_primary_container.composeColor,
    secondary               = SR.colors.sr_secondary.composeColor,
    onSecondary             = SR.colors.sr_on_secondary.composeColor,
    secondaryContainer      = SR.colors.sr_secondary_container.composeColor,
    onSecondaryContainer    = SR.colors.sr_on_secondary_container.composeColor,
    tertiary                = SR.colors.sr_tertiary.composeColor,
    onTertiary              = SR.colors.sr_on_tertiary.composeColor,
    tertiaryContainer       = SR.colors.sr_tertiary_container.composeColor,
    onTertiaryContainer     = SR.colors.sr_on_tertiary_container.composeColor,
    error                   = SR.colors.sr_error.composeColor,
    onError                 = SR.colors.sr_on_error.composeColor,
    errorContainer          = SR.colors.sr_error_container.composeColor,
    onErrorContainer        = SR.colors.sr_on_error_container.composeColor,
    surfaceDim              = SR.colors.sr_surface_dim.composeColor,
    surface                 = SR.colors.sr_surface.composeColor,
    surfaceBright           = SR.colors.sr_surface_bright.composeColor,
    inverseSurface          = SR.colors.sr_inverse_surface.composeColor,
    inverseOnSurface        = SR.colors.sr_inverse_on_surface.composeColor,
    inversePrimary          = SR.colors.sr_inverse_primary.composeColor,
    surfaceContainerLowest  = SR.colors.sr_surface_container_lowest.composeColor,
    surfaceContainerLow     = SR.colors.sr_surface_container_low.composeColor,
    surfaceContainer        = SR.colors.sr_surface_container.composeColor,
    surfaceContainerHigh    = SR.colors.sr_surface_container_high.composeColor,
    surfaceContainerHighest = SR.colors.sr_surface_container_highest.composeColor,
    onSurface               = SR.colors.sr_on_surface.composeColor,
    onSurfaceVariant        = SR.colors.sr_on_surface_variant.composeColor,
    outline                 = SR.colors.sr_outline.composeColor,
    outlineVariant          = SR.colors.sr_outline_variant.composeColor,
    scrim                   = SR.colors.sr_scrim.composeColor
)

val defaultDarkColorScheme = darkColorScheme(
    background              = SR.colors.sr_background_dm.composeColor,
    onBackground            = SR.colors.sr_on_background_dm.composeColor,
    primary                 = SR.colors.sr_primary_dm.composeColor,
    onPrimary               = SR.colors.sr_on_primary_dm.composeColor,
    primaryContainer        = SR.colors.sr_primary_container_dm.composeColor,
    onPrimaryContainer      = SR.colors.sr_on_primary_container_dm.composeColor,
    secondary               = SR.colors.sr_secondary_dm.composeColor,
    onSecondary             = SR.colors.sr_on_secondary_dm.composeColor,
    secondaryContainer      = SR.colors.sr_secondary_container_dm.composeColor,
    onSecondaryContainer    = SR.colors.sr_on_secondary_container_dm.composeColor,
    tertiary                = SR.colors.sr_tertiary_dm.composeColor,
    onTertiary              = SR.colors.sr_on_tertiary_dm.composeColor,
    tertiaryContainer       = SR.colors.sr_tertiary_container_dm.composeColor,
    onTertiaryContainer     = SR.colors.sr_on_tertiary_container_dm.composeColor,
    error                   = SR.colors.sr_error_dm.composeColor,
    onError                 = SR.colors.sr_on_error_dm.composeColor,
    errorContainer          = SR.colors.sr_error_container_dm.composeColor,
    onErrorContainer        = SR.colors.sr_on_error_container_dm.composeColor,
    surfaceDim              = SR.colors.sr_surface_dim_dm.composeColor,
    surface                 = SR.colors.sr_surface_dm.composeColor,
    surfaceBright           = SR.colors.sr_surface_bright_dm.composeColor,
    inverseSurface          = SR.colors.sr_inverse_surface_dm.composeColor,
    inverseOnSurface        = SR.colors.sr_inverse_on_surface_dm.composeColor,
    inversePrimary          = SR.colors.sr_inverse_primary_dm.composeColor,
    surfaceContainerLowest  = SR.colors.sr_surface_container_lowest_dm.composeColor,
    surfaceContainerLow     = SR.colors.sr_surface_container_low_dm.composeColor,
    surfaceContainer        = SR.colors.sr_surface_container_dm.composeColor,
    surfaceContainerHigh    = SR.colors.sr_surface_container_high_dm.composeColor,
    surfaceContainerHighest = SR.colors.sr_surface_container_highest_dm.composeColor,
    onSurface               = SR.colors.sr_on_surface_dm.composeColor,
    onSurfaceVariant        = SR.colors.sr_on_surface_variant_dm.composeColor,
    outline                 = SR.colors.sr_outline_dm.composeColor,
    outlineVariant          = SR.colors.sr_outline_variant_dm.composeColor,
    scrim                   = SR.colors.sr_scrim_dm.composeColor
)

// Typography scale: Display, Headline, Title, Body, Label (Large/Medium/Small) – Google Sans
// Letter spacing from Figma: absolute values in px/pt → use as .sp (e.g. +0.5 → 0.5.sp)
fun getAppThemeTypography(colors: ColorScheme) = Typography(
    // Display: Large 57 / -0.25, Medium 45 SemiBold / 0, Small 36 SemiBold / 0
    displayLarge = TextStyle(
        fontFamily      = LocalTheme.googleSans,
        fontWeight      = FontWeight.Normal,
        fontSize        = 57.sp,
        lineHeight      = 64.sp,
        letterSpacing   = (-0.25).sp,
        color           = colors.onSurface
    ),
    displayMedium = TextStyle(
        fontFamily      = LocalTheme.googleSans,
        fontWeight      = FontWeight.SemiBold,
        fontSize        = 45.sp,
        lineHeight      = 52.sp,
        letterSpacing   = 0.sp,
        color           = colors.onSurface
    ),
    displaySmall = TextStyle(
        fontFamily      = LocalTheme.googleSans,
        fontWeight      = FontWeight.SemiBold,
        fontSize        = 36.sp,
        lineHeight      = 44.sp,
        letterSpacing   = 0.sp,
        color           = colors.onSurface
    ),
    // Headline: Large/Medium/Small – Medium weight, 32/28/24, letter spacing 0
    headlineLarge = TextStyle(
        fontFamily      = LocalTheme.googleSans,
        fontWeight      = FontWeight.Medium,
        fontSize        = 32.sp,
        lineHeight      = 40.sp,
        letterSpacing   = 0.sp,
        color           = colors.onSurface
    ),
    headlineMedium = TextStyle(
        fontFamily      = LocalTheme.googleSans,
        fontWeight      = FontWeight.Medium,
        fontSize        = 28.sp,
        lineHeight      = 36.sp,
        letterSpacing   = 0.sp,
        color           = colors.onSurface
    ),
    headlineSmall = TextStyle(
        fontFamily      = LocalTheme.googleSans,
        fontWeight      = FontWeight.Medium,
        fontSize        = 24.sp,
        lineHeight      = 32.sp,
        letterSpacing   = 0.sp,
        color           = colors.onSurface
    ),
    // Title: Large 22 / 0, Medium 16 / +0.15, Small 14 SemiBold / +0.1
    titleLarge = TextStyle(
        fontFamily      = LocalTheme.googleSans,
        fontWeight      = FontWeight.Normal,
        fontSize        = 22.sp,
        lineHeight      = 28.sp,
        letterSpacing   = 0.sp,
        color           = colors.onSurface
    ),
    titleMedium = TextStyle(
        fontFamily      = LocalTheme.googleSans,
        fontWeight      = FontWeight.Medium,
        fontSize        = 16.sp,
        lineHeight      = 24.sp,
        letterSpacing   = 0.15.sp,
        color           = colors.onSurface
    ),
    titleSmall = TextStyle(
        fontFamily      = LocalTheme.googleSans,
        fontWeight      = FontWeight.SemiBold,
        fontSize        = 14.sp,
        lineHeight      = 16.sp,
        letterSpacing   = 0.1.sp,
        color           = colors.onSurface
    ),
    // Body: Large 16 / +0.5, Medium 14 / +0.25, Small 12 / +0.4
    bodyLarge = TextStyle(
        fontFamily      = LocalTheme.googleSans,
        fontWeight      = FontWeight.Normal,
        fontSize        = 16.sp,
        lineHeight      = 24.sp,
        letterSpacing   = 0.5.sp,
        color           = colors.onSurface
    ),
    bodyMedium = TextStyle(
        fontFamily      = LocalTheme.googleSans,
        fontWeight      = FontWeight.Normal,
        fontSize        = 14.sp,
        lineHeight      = 20.sp,
        letterSpacing   = 0.25.sp,
        color           = colors.onSurface
    ),
    bodySmall = TextStyle(
        fontFamily      = LocalTheme.googleSans,
        fontWeight      = FontWeight.Normal,
        fontSize        = 12.sp,
        lineHeight      = 16.sp,
        letterSpacing   = 0.4.sp,
        color           = colors.onSurface
    ),
    // Label: Large 14 / +0.1, Medium 12 / +0.5, Small 11 / +0.5 – Medium weight
    labelLarge = TextStyle(
        fontFamily      = LocalTheme.googleSans,
        fontWeight      = FontWeight.Medium,
        fontSize        = 14.sp,
        lineHeight      = 20.sp,
        letterSpacing   = 0.1.sp,
        color           = colors.onSurface
    ),
    labelMedium = TextStyle(
        fontFamily      = LocalTheme.googleSans,
        fontWeight      = FontWeight.Medium,
        fontSize        = 12.sp,
        lineHeight      = 16.sp,
        letterSpacing   = 0.5.sp,
        color           = colors.onSurface
    ),
    labelSmall = TextStyle(
        fontFamily      = LocalTheme.googleSans,
        fontWeight      = FontWeight.Medium,
        fontSize        = 10.sp,
        lineHeight      = 16.sp,
        letterSpacing   = 0.5.sp,
        color           = colors.onSurface
    )
)

val Typography.extraSmall
    get() = TextStyle(
        fontFamily      = LocalTheme.carosoft,
        fontWeight      = FontWeight.ExtraLight,
        fontSize        = 11.sp,
        lineHeight      = 16.sp,
        letterSpacing   = 0.4.sp,
        color           = currentTheme.value.colors.onSurface
    )

val Typography.navItem
    get() = TextStyle(
        fontFamily      = LocalTheme.carosoft,
        fontWeight      = FontWeight.ExtraLight,
        fontSize        = 10.sp,
        lineHeight      = 16.sp,
        letterSpacing   = 0.2.sp,
        color           = currentTheme.value.colors.onSurface
    )

val Typography.amountSmall
    get() = TextStyle(
        fontFamily      = LocalTheme.carosoft,
        fontWeight      = FontWeight.ExtraLight,
        fontSize        = 24.sp,
        lineHeight      = 28.sp,
        letterSpacing   = 0.sp,
        color           = currentTheme.value.colors.onSurface
    )

val Typography.amountMedium
    get() = TextStyle(
        fontFamily      = LocalTheme.carosoft,
        fontWeight      = FontWeight.ExtraLight,
        fontSize        = 34.sp,
        lineHeight      = 30.sp,
        letterSpacing   = 0.sp,
        color           = currentTheme.value.colors.onSurface
    )

val Typography.amountAccount
    get() = TextStyle(
        fontFamily      = LocalTheme.carosoft,
        fontWeight      = FontWeight.ExtraLight,
        fontSize        = 30.sp,
        lineHeight      = 32.sp,
        letterSpacing   = 0.sp,
        color           = currentTheme.value.colors.onSurface
    )

/** Figma: T Body Large Regular 18 – category tabs (letter spacing +0.5 like Body Large). */
val Typography.bodyLarge18
    get() = TextStyle(
        fontFamily      = LocalTheme.googleSans,
        fontWeight      = FontWeight.Normal,
        fontSize        = 18.sp,
        lineHeight      = 24.sp,
        letterSpacing   = 0.5.sp,
        color           = currentTheme.value.colors.onSurface
    )